/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import RegularTemplate from '@/components/templates/RegularTemplate'
import { urlsGeneral } from '@/configs/urls'
import RegisterForm from './components/RegisterForm'
import SuccessMessage from './components/SuccessMessage'
import { ASSET_WAS_REGISTERED } from '@/configs/strings/message'
import {
  REGISTER_NEW_ASSET_DESCRIPTION,
  REGISTER_NEW_ASSET_TITLE
} from '@/configs/strings/assets'
import { useLocation } from 'react-router-dom'
import { postAsset } from '@/services/assets'
import { useAuth0 } from '@auth0/auth0-react'
import ScreenLoader from '@/components/loaders/ScreenLoader'
import { useHandlerFetchErrors } from '@/hooks/fetch/useHandlerFetchErrors'
import NonPermission from '@/components/messages/NonPermission'
import { arrayBufferToBase64 } from '@/utils/files'

const RegisterAsset = () => {
  const handleFetchErrors = useHandlerFetchErrors()
  const location = useLocation()
  const [errorScreen] = useState()
  const [isUploading, setIsUploading] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [userNickname] = useState('')
  const [successMsg, setMessage] = useState('')
  const [formValues, setFormValues] = useState({})
  const selectedFile = location.state?.selectedFile || null
  const metadata = location.state?.metadata || null
  const resourceHash = location.state?.metadata.resourceHash || null
  const assetStorageId = location.state?.metadata.assetStorageId || null
  const dataSize = location.state?.metadata.dataSize || null
  const fileType = location.state?.metadata.fileType || null
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const normalizeString = (str) => str
    .toUpperCase()
    .replace('-', '_')
    .replace(' ', '_')
  const onSubmit = async ({
    data,
    creators,
    publishers
  }) => {
    const { attestationType, authorizedAgent, ...currentData } = data
    const currentAuthorizedAgent = attestationType === 'AUTHORIZED_AGENT'
      ? { authorizedAgent: data.authorizedAgent }
      : {}
    setFormValues({
      ...currentData,
      ...currentAuthorizedAgent,
      attestationType,
      creators: creators.toString(),
      publishers: publishers.toString()
    })
    setIsUploading(true)
    setIsProcessing(true)

    const coverBase64 = metadata.cover ? arrayBufferToBase64(metadata.cover.buffer) : ''

    const postData = {
      ...currentData,
      ...currentAuthorizedAgent,
      ipType: normalizeString(currentData.ipType),
      attestationType: normalizeString(attestationType),
      creators,
      publishers,
      metadata: metadata.metadata,
      resourceHash,
      assetStorageId,
      dataSize,
      fileType,
      filename: selectedFile.name,
      cover: coverBase64
    }

    postAsset(
      postData, getAccessToken)
      .then(res => {
        setIsProcessing(false)
        setIsUploading(false)
        setMessage('Success')
      })
      .catch(errors => {
        handleFetchErrors(errors)
      })
  }

  return (
    <RegularTemplate
      errorScreen={errorScreen}
      description={REGISTER_NEW_ASSET_DESCRIPTION}
      title={REGISTER_NEW_ASSET_TITLE}
    >
      { isProcessing && <ScreenLoader /> }
      <>
        { !metadata && <NonPermission /> }
        { isUploading && <ScreenLoader /> }
        { !isProcessing && !successMsg && metadata
          ? <RegisterForm
              cancelUrl={`/${urlsGeneral.collections}`}
              onSubmit={onSubmit}
              currentCreators={metadata.creators || []}
              currentPublishers={metadata.publishers || []}
              currentMetadata={metadata.metadata || {}}
              currentTitle={metadata.title || ''}
              currentDescription={metadata.description || ''}
              selectedFile={selectedFile}
              userNickname={userNickname}
              fileType={fileType}
            />
          : successMsg && <SuccessMessage
            isBackgroundProcess={false}
            formValues={formValues}
            title={ASSET_WAS_REGISTERED}
          />
        }
      </>
    </RegularTemplate>
  )
}

export default RegisterAsset
